import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  input,
  output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Guid, once } from '@web/utils';
import { Observable } from 'rxjs';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { HttpErrorService } from '@web/common';
import { HttpErrorResponse } from '@angular/common/http';
import { WarehouseReleaseDataSourceService } from '../../services/warehouse-release-data-source.service';
import { WarehouseReleaseFormData } from '../../models/warehouse-release-form-data';
import { WarehouseReleaseFormComponent } from '../../components/warehouse-release-form/warehouse-release-form.component';

@Component({
  selector: 'app-warehouse-release-edit',
  imports: [
    CommonModule,
    SpinnerFilledComponent,
    AlertsComponent,
    WarehouseReleaseFormComponent,
  ],
  templateUrl: './warehouse-release-edit.component.html',
  styleUrl: './warehouse-release-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class WarehouseReleaseEditComponent {
  /* prettier-ignore */
  private readonly warehouseReleaseDataSource = inject(WarehouseReleaseDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  public readonly id = input.required<Guid>();
  public readonly fileUploadVisibility = input(false);
  public readonly submitSuccessEvent = output();

  protected data$!: Observable<WarehouseReleaseFormData>;

  public constructor() {
    effect(() => this.configureDataSource());
  }

  public handleSubmitSuccessEvent(data: WarehouseReleaseFormData): void {
    this.warehouseReleaseDataSource
      .updateByFormData(this.id(), data)
      .pipe(once())
      .subscribe({
        next: () => {
          this.notifySubmitSuccessEvent();
        },
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private notifySubmitSuccessEvent(): void {
    this.submitSuccessEvent.emit();
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    this.alerts.showMultiple((builder) =>
      this.httpError
        .extract(response)
        .messages()
        .map((message) => builder.buildFailure(message)),
    );
  }

  private configureDataSource(): void {
    this.data$ = this.warehouseReleaseDataSource.fetchDetailsFormById(
      this.id(),
    );

    this.changeDetectorRef.detectChanges();
  }
}
